import { useState, useEffect, useMemo } from "react";
import {
  useGetMemberSlotCapacityQuery,
  useUpdateMemberSlotAllocationMutation,
} from "../../../store/admin/settings/memberSlotSlice";
import { PageTitle } from "../modules/PageHeaders";
import { CardComp, ButtonComp, PlainTable } from "../../../components";
import { toast } from "react-hot-toast";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";

export default function MemberSlotCapacity() {
  const { data, isLoading, isError, error, refetch } = useGetMemberSlotCapacityQuery();
  const [updateAllocation, { isLoading: isUpdating }] = useUpdateMemberSlotAllocationMutation();

  const [edits, setEdits] = useState({}); // branchId -> allocation number

  useEffect(() => {
    if (data?.branches) {
      const initial = {};
      data.branches.forEach((b) => {
        initial[b.id] = b.member_slot_allocation;
      });
      setEdits(initial);
    }
  }, [data?.branches]);

  const capacity = data?.member_slot_capacity ?? 0;
  const totalAllocated = data?.total_allocated ?? 0;
  const totalUsed = data?.total_used ?? 0;
  const availablePool = data?.available_pool ?? 0;
  const branches = data?.branches ?? [];

  const handleAllocationChange = (branchId, value) => {
    const num = Math.max(0, parseInt(value, 10) || 0);
    setEdits((prev) => ({ ...prev, [branchId]: num }));
  };

  const sumEdits = () => {
    return Object.values(edits).reduce((s, n) => s + (n || 0), 0);
  };

  const handleSave = async () => {
    const totalCapacity = capacity;
    const sum = sumEdits();
    if (sum > totalCapacity) {
      toast.error(
        `Total allocations (${sum}) cannot exceed member slot capacity (${totalCapacity}).`
      );
      return;
    }

    for (const b of branches) {
      const newVal = edits[b.id];
      if (newVal === undefined || newVal === b.member_slot_allocation) continue;
      if (newVal < b.member_count) {
        toast.error(
          `Allocation for "${b.name}" cannot be less than current members (${b.member_count}).`
        );
        return;
      }
    }

    const toastId = toast.loading("Saving…");
    try {
      let done = 0;
      for (const b of branches) {
        const newVal = edits[b.id];
        if (newVal === undefined || newVal === b.member_slot_allocation) continue;
        await updateAllocation({ branchId: b.id, member_slot_allocation: newVal }).unwrap();
        done++;
      }
      if (done > 0) {
        toast.success("Member slot allocations updated.", { id: toastId });
        refetch();
      } else {
        toast.dismiss(toastId);
      }
    } catch (err) {
      const msg =
        err?.data?.message || err?.error?.message || err?.message || "Failed to update.";
      toast.error(msg, { id: toastId });
    }
  };

  const hasChanges = branches.some(
    (b) => edits[b.id] !== undefined && edits[b.id] !== b.member_slot_allocation
  );
  const overCapacity = sumEdits() > capacity;
  const anyBelowMemberCount = branches.some(
    (b) => edits[b.id] !== undefined && edits[b.id] < b.member_count
  );
  const summaryCards = [
    { label: "Total capacity", value: capacity },
    { label: "Total allocated", value: totalAllocated },
    { label: "Total used (members)", value: totalUsed },
    { label: "Available pool", value: availablePool, accent: true },
  ];
  const tableColumns = useMemo(
    () => [
      {
        name: "Branch",
        selector: (row) => row?.name || "-",
        sortable: true,
      },
      {
        name: "Allocated",
        selector: (row) => row?.member_slot_allocation ?? 0,
      },
      {
        name: "Used",
        selector: (row) => row?.member_count ?? 0,
      },
      {
        name: "Unused",
        selector: (row) => row?.unused ?? 0,
      },
      {
        name: "Set member slots",
        cell: (row) => (
          <input
            type="number"
            min={row?.member_count ?? 0}
            className="form-control form-control-sm"
            style={{
              width: "120px",
              background: "#fafafa",
              border: "1px solid #ced4da65",
              borderRadius: "5px",
            }}
            value={edits[row?.id] ?? row?.member_slot_allocation ?? 0}
            onChange={(e) => handleAllocationChange(row?.id, e.target.value)}
          />
        ),
      },
    ],
    [edits]
  );

  if (isError) {
    return (
      <div className="p-4">
        <PageTitle title="Member slot capacity" />
        <CardComp>
          <div className="alert alert-danger mb-0">
            {error?.data?.message || "Failed to load member slot capacity."}
          </div>
        </CardComp>
      </div>
    );
  }

  return (
    <div className="p-4">
      <PageTitle title="Member slot capacity" />

      <p className="text-muted mb-4">
        View and allocate member slots across branches. Total allocations cannot exceed your
        subscription capacity.
      </p>

      <CardComp className="mb-4">
        <h5 className="mb-3">Summary</h5>
        <div className="row g-3">
          {summaryCards.map((item) => (
            <div className="col-6 col-md-3" key={item.label}>
              <div
                className="h-100"
                style={{
                  background: item?.accent ? "var(--color-secondary-soft)" : "#f7f7f7",
                  border: `1px solid ${item?.accent ? "var(--color-secondary-soft)" : "#e0e0e0"}`,
                  borderRadius: "10px",
                  padding: "14px 12px",
                }}
              >
                <div
                  className="small"
                  style={{
                    color: item?.accent ? "var(--color-secondary)" : "#525252",
                  }}
                >
                  {item.label}
                </div>
                <div className="fw-semibold mt-1" style={{ fontSize: "20px", color: "#383838" }}>
                  {item.value}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardComp>

      <CardComp>
        <h5 className="mb-1">Per-branch allocation</h5>
        <p className="text-muted small mb-3">
          Each branch allocation must be at least its current member count.
        </p>
        <PlainTable
          data={branches}
          columns={tableColumns}
          progressPending={isLoading}
          progressComponent={<TableSkeletonLoader rows={8} columns={5} />}
          message={<div className="py-4 text-muted">No branches.</div>}
          marginBottom="1rem"
        />

        {branches.length > 0 && (
          <div className="mt-3 d-flex flex-column flex-md-row align-items-md-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <ButtonComp
                btnText="Save changes"
                loading={isUpdating}
                onClick={handleSave}
                BorderColor="var(--color-primary)"
                ButtonInlineStyle={{
                  minWidth: "200px",
                  borderRadius: "10px",
                  padding: "10px 16px",
                }}
                disabled={!hasChanges || isUpdating || overCapacity || anyBelowMemberCount}
              />
              {hasChanges && (
                <span
                  className="badge"
                  style={{ background: "#fcefe2", color: "#da6900", fontWeight: 500 }}
                >
                  Unsaved changes
                </span>
              )}
            </div>

            {(overCapacity || anyBelowMemberCount) && (
              <div
                className="small px-3 py-2"
                style={{
                  background: "#fff5f5",
                  color: "#ff4343",
                  borderLeft: "3px solid #ff4343",
                  borderRadius: "6px",
                }}
              >
                {overCapacity
                  ? `Total allocations exceed capacity (${sumEdits()} > ${capacity}).`
                  : "One or more allocations are below current member count."}
              </div>
            )}
          </div>
        )}
      </CardComp>
    </div>
  );
}
